/**
 * Functions to generate chart configuration options for the dashboard page.
 */
const colorPalette = [
    "#117ACB",
    "#04373D",
    "#068190",
    "#0FBEF9",
    "#B45DFF",
    "#674485",
    "#117ACB",
];

/**
 * Function to get a random color that hasn’t been used yet.
 *
 * @param {array} usedColors
 * @return {array} color
 */
const getRandomColor = (usedColors) => {
    const availableColors = colorPalette.filter(
        (color) => !usedColors.includes(color)
    );
    const randomIndex = Math.floor(Math.random() * availableColors.length);
    return availableColors[randomIndex];
};

/**
 * Function to get configuration options for an Acute Toxicity - Lethal chart.
 *
 * @param {object} dataDash2
 * @return {object} options
 */
export function getCategoryChartOptions(dataDash2) {
    if (
        !dataDash2 ||
        typeof dataDash2.oralcategory1_count === "undefined" ||
        typeof dataDash2.oralcategory2_count === "undefined" ||
        typeof dataDash2.inhalationcategory1_count === "undefined" ||
        typeof dataDash2.inhalationcategory2_count === "undefined" ||
        typeof dataDash2.cutaneouscategory1_count === "undefined" ||
        typeof dataDash2.cutaneouscategory2_count === "undefined"
    ) {
        return {};
    }

    const categories = ["Ingestion", "Dermal", "Inhalation"];
    const series2011 = [
        dataDash2.oralcategory1_count,
        dataDash2.cutaneouscategory1_count,
        dataDash2.inhalationcategory1_count,
    ];
    const series2012 = [
        dataDash2.oralcategory2_count,
        dataDash2.cutaneouscategory2_count,
        dataDash2.inhalationcategory2_count,
    ];

    return {
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: "shadow",
            },
        },
        legend: {
            data: ["Category 1", "Category 2"],
            textStyle: {
                fontFamily: "Poppins",
                color: "#333",
            },
            top: "5%",
            icon: "circle",
        },
        grid: {
            top: "15%",
            left: "3%",
            right: "4%",
            bottom: "3%",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: categories,
            axisLabel: {
                fontFamily: "Poppins",
                color: "#333",
                fontSize: 12,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: false,
            },
        },
        yAxis: {
            type: "value",
            splitLine: {
                lineStyle: {
                    type: "dashed",
                    color: "#E0E0E0",
                },
            },
            axisLabel: {
                fontFamily: "Poppins",
                color: "#333",
                fontSize: 12,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: false,
            },
        },
        series: [
            {
                name: "Category 1",
                type: "bar",
                data: series2011,
                itemStyle: {
                    color: "#0CB1F7",
                    borderRadius: [15, 15, 0, 0],
                },
                barWidth: "20%",
                barGap: "100%",
            },
            {
                name: "Category 2",
                type: "bar",
                data: series2012,
                itemStyle: {
                    color: "#04373D",
                    borderRadius: [15, 15, 0, 0],
                },
                barWidth: "20%",
            },
        ],
    };
}

/**
 * Function to get configuration options for a Carcinogenicity chart.
 *
 * @param {object} dataDash2
 * @return {object} options
 */
export function getCarcinogenicityChartOptions(dataDash2) {
    if (
        !dataDash2 ||
        typeof dataDash2.carcinogenic_group_1_count === "undefined" ||
        typeof dataDash2.carcinogenic_group_2A_count === "undefined" ||
        typeof dataDash2.carcinogenic_group_2B_count === "undefined" ||
        typeof dataDash2.carcinogenic_reported_by_sds_count === "undefined"
    ) {
        return {};
    }

    const series = [
        {
            name: "Carcinogenic to humans",
            value: dataDash2.carcinogenic_group_1_count,
            color: "#068190",
        },
        {
            name: "Probably carcinogenic to humans",
            value: dataDash2.carcinogenic_group_2A_count,
            color: "#04373D",
        },
        {
            name: "Possibly carcinogenic to humans",
            value: dataDash2.carcinogenic_group_2B_count,
            color: "#117ACB",
        },
        {
            name: "Reported by SDS",
            value: dataDash2.carcinogenic_reported_by_sds_count,
            color: "#0FBEF9",
        },
    ];

    const totalCarcinogenic = series.reduce((sum, item) => sum + item.value, 0);
    const maxValue = Math.max(...series.map((item) => item.value));
    const yAxisMax = Math.ceil(maxValue * 1.2);

    return {
        title: {
            text: `${totalCarcinogenic} Carcinogenic`,
            left: "20px",
            top: "10px",
            textStyle: {
                fontFamily: "Poppins",
                fontSize: "16px",
                fontWeight: 500,
                color: "#333",
                rich: {
                    total: {
                        fontSize: "24px",
                        fontWeight: 600,
                        padding: [0, 8, 0, 0],
                    },
                },
            },
        },
        tooltip: {
            trigger: "item",
            axisPointer: {
                type: "none",
            },
            textStyle: {
                fontFamily: "Poppins",
            },
        },
        grid: {
            left: "3%",
            right: "4%",
            bottom: "25%",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: [""],
            axisLabel: {
                show: false,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: false,
            },
        },
        yAxis: {
            type: "value",
            max: yAxisMax,
            splitNumber: 6,
            splitLine: {
                lineStyle: {
                    type: "dashed",
                    color: "#E0E0E0",
                },
            },
            axisLabel: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 12,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: false,
            },
        },
        legend: {
            data: series.map((item) => item.name),
            bottom: "0",
            left: "center",
            itemWidth: 8,
            itemHeight: 8,
            textStyle: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 11,
            },
            itemGap: 5,
            icon: "circle",
            formatter: function (name) {
                const item = series.find((s) => s.name === name);
                return `${name} (${item.value})`;
            },
        },
        series: series.map((item) => ({
            name: item.name,
            type: "bar",
            data: [item.value],
            itemStyle: {
                color: item.color,
                borderRadius: [15, 15, 0, 0],
            },
            barWidth: '8%',
            barGap: 1.5,
            label: {
                show: true,
                position: "top",
                distance: 5,
                fontFamily: "Poppins",
                fontSize: 12,
                color: "#333",
            },
        })),
    };
}

/**
 * Function to get configuration options for a Reprotoxicity chart.
 *
 * @param {object} dataDash2
 * @return {object} options
 */
export function getReprotoxicityChartOptions(dataDash2) {
    if (
        !dataDash2 ||
        typeof dataDash2.product_reprotoxic_with_affected_count === "undefined" ||
        typeof dataDash2.product_reprotoxic_not_affected_count === "undefined"
    ) {
        return {};
    }

    return {
        tooltip: {
            trigger: "item",
            textStyle: {
                fontFamily: "Poppins",
            },
        },
        legend: {
            orient: "vertical",
            left: "65%",
            top: "middle",
            type: "scroll",
            width: "35%",
            textStyle: {
                fontSize: 11,
                overflow: "break",
                fontFamily: "Poppins",
            },
            icon: "circle",
        },
        series: [
            {
                name: "Reprotoxic products",
                type: "pie",
                radius: ["40%", "70%"],
                center: ["35%", "50%"],
                avoidLabelOverlap: false,
                itemStyle: {
                    borderRadius: 6,
                    borderColor: "#fff",
                    borderWidth: 2,
                },
                label: {
                    show: true,
                    position: "inside",
                    formatter: "{c}",
                    fontSize: 14,
                    fontWeight: "bold",
                    color: "#fff",
                    textShadowColor: "rgba(0, 0, 0, 0.5)",
                    textShadowBlur: 3,
                    textShadowOffsetX: 1,
                    textShadowOffsetY: 1,
                    fontFamily: "Poppins",
                },
                labelLine: {
                    show: false,
                },
                emphasis: {
                    label: {
                        show: true,
                        fontSize: 16,
                        fontWeight: "bold",
                        fontFamily: "Poppins",
                    },
                    itemStyle: {
                        shadowBlur: 10,
                        shadowOffsetX: 0,
                        shadowColor: "rgba(0, 0, 0, 0.5)",
                    },
                },
                data: [
                    {
                        value: dataDash2.product_reprotoxic_with_affected_count,
                        name: "With affection",
                        itemStyle: {
                            color: "#117ACB",
                        },
                    },
                    {
                        value: dataDash2.product_reprotoxic_not_affected_count,
                        name: "Without affection",
                        itemStyle: {
                            color: "#0FBEF9",
                        },
                    },
                ],
            },
        ],
    };
}

/**
 * Function to get configuration options for an Ototoxicity chart.
 *
 * @param {object} dataDash2
 * @return {object} options
 */
export function getOtoxicityChartOptions(dataDash2) {
    if (
        !dataDash2 ||
        typeof dataDash2.product_ototoxic_not_affected_count === "undefined" ||
        typeof dataDash2.product_ototoxic_with_affected_count === "undefined"
    ) {
        return {};
    }

    return {
        tooltip: {
            trigger: "item",
            textStyle: {
                fontFamily: "Poppins",
            },
        },
        legend: {
            orient: "vertical",
            left: "65%",
            top: "middle",
            type: "scroll",
            width: "35%",
            textStyle: {
                fontSize: 11,
                overflow: "break",
                fontFamily: "Poppins",
            },
            icon: "circle",
        },
        series: [
            {
                name: "Ototoxic products",
                type: "pie",
                radius: ["40%", "70%"],
                center: ["35%", "50%"],
                avoidLabelOverlap: false,
                itemStyle: {
                    borderRadius: 6,
                    borderColor: "#fff",
                    borderWidth: 2,
                },
                label: {
                    show: true,
                    position: "inside",
                    formatter: "{c}",
                    fontSize: 14,
                    fontWeight: "bold",
                    color: "#fff",
                    textShadowColor: "rgba(0, 0, 0, 0.5)",
                    textShadowBlur: 3,
                    textShadowOffsetX: 1,
                    textShadowOffsetY: 1,
                    fontFamily: "Poppins",
                },
                labelLine: {
                    show: false,
                },
                emphasis: {
                    label: {
                        show: true,
                        fontSize: 16,
                        fontWeight: "bold",
                        fontFamily: "Poppins",
                    },
                    itemStyle: {
                        shadowBlur: 10,
                        shadowOffsetX: 0,
                        shadowColor: "rgba(0, 0, 0, 0.5)",
                    },
                },
                data: [
                    {
                        value: dataDash2.product_ototoxic_with_affected_count,
                        name: "With affection",
                        itemStyle: {
                            color: "#117ACB",
                        },
                    },
                    {
                        value: dataDash2.product_ototoxic_not_affected_count,
                        name: "Without affection",
                        itemStyle: {
                            color: "#0FBEF9",
                        },
                    },
                ],
            },
        ],
    };
}

/**
 * Function to get configuration options for a Mutagenicity chart.
 *
 * @param {object} dataDash2
 * @return {object} options
 */
export function getMutagenicityChartOptions(dataDash2) {
    if (
        !dataDash2 ||
        typeof dataDash2.product_mutagenic_with_affected_count === "undefined" ||
        typeof dataDash2.product_mutagenic_not_affected_count === "undefined"
    ) {
        return {};
    }

    return {
        tooltip: {
            trigger: "item",
            textStyle: {
                fontFamily: "Poppins",
            },
        },
        legend: {
            orient: "vertical",
            left: "65%",
            top: "middle",
            type: "scroll",
            width: "35%",
            textStyle: {
                fontSize: 11,
                overflow: "break",
                fontFamily: "Poppins",
            },
            icon: "circle",
        },
        series: [
            {
                name: "Mutagenic products",
                type: "pie",
                radius: ["40%", "70%"],
                center: ["35%", "50%"],
                avoidLabelOverlap: false,
                itemStyle: {
                    borderRadius: 6,
                    borderColor: "#fff",
                    borderWidth: 2,
                },
                label: {
                    show: true,
                    position: "inside",
                    formatter: "{c}",
                    fontSize: 14,
                    fontWeight: "bold",
                    color: "#fff",
                    textShadowColor: "rgba(0, 0, 0, 0.5)",
                    textShadowBlur: 3,
                    textShadowOffsetX: 1,
                    textShadowOffsetY: 1,
                    fontFamily: "Poppins",
                },
                labelLine: {
                    show: false,
                },
                emphasis: {
                    label: {
                        show: true,
                        fontSize: 16,
                        fontWeight: "bold",
                        fontFamily: "Poppins",
                    },
                    itemStyle: {
                        shadowBlur: 10,
                        shadowOffsetX: 0,
                        shadowColor: "rgba(0, 0, 0, 0.5)",
                    },
                },
                data: [
                    {
                        value: dataDash2.product_mutagenic_with_affected_count,
                        name: "With affection",
                        itemStyle: {
                            color: "#117ACB",
                        },
                    },
                    {
                        value: dataDash2.product_mutagenic_not_affected_count,
                        name: "Without affection",
                        itemStyle: {
                            color: "#0FBEF9",
                        },
                    },
                ],
            },
        ],
    };
}

/**
 * Function to get configuration options for a Normative Prioritization Components chart.
 *
 * @param {object} data
 * @param {number} page
 * @param {number} itemsPerPage
 * @return {object} options
 */
export const getPriorityChartOptions = (data, page = 1, itemsPerPage = 10) => {
    if (!data || !data.components_priority_normative) return {};

    const sortedData = [...data.components_priority_normative].sort(
        (a, b) => b.value - a.value
    );

    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    const paginatedData = sortedData.slice(startIndex, endIndex);

    const usedColors = [];
    const colors = paginatedData.map(() => {
        const color = getRandomColor(usedColors);
        usedColors.push(color);
        return color;
    });

    return {
        tooltip: {
            trigger: "item",
            formatter: (params) => {
                return `${params.name}<br/>Quantity: ${params.value}`;
            },
        },
        legend: {
            orient: "vertical",
            left: "65%",
            top: "middle",
            type: "scroll",
            width: "35%",
            formatter: (name) => {
                const item = paginatedData.find((d) => d.name === name);
                return `${name.substring(0, 15)}${
                    name.length > 15 ? "..." : ""
                } (${item.value})`;
            },
            textStyle: {
                fontSize: 11,
                overflow: "break",
                fontFamily: "Poppins",
            },
            icon: "circle",
        },
        series: [
            {
                name: "Components",
                type: "pie",
                radius: ["40%", "70%"],
                center: ["35%", "50%"],
                avoidLabelOverlap: false,
                itemStyle: {
                    borderRadius: 6,
                    borderColor: "#fff",
                    borderWidth: 2,
                },
                label: {
                    show: true,
                    position: "inside",
                    formatter: "{c}",
                    fontSize: 14,
                    fontWeight: "bold",
                    color: "#fff",
                    textShadowColor: "rgba(0, 0, 0, 0.5)",
                    textShadowBlur: 3,
                    textShadowOffsetX: 1,
                    textShadowOffsetY: 1,
                },
                labelLine: {
                    show: false,
                },
                emphasis: {
                    label: {
                        show: true,
                        fontSize: 16,
                        fontWeight: "bold",
                    },
                    itemStyle: {
                        shadowBlur: 10,
                        shadowOffsetX: 0,
                        shadowColor: "rgba(0, 0, 0, 0.5)",
                    },
                },
                data: paginatedData.map((item, index) => ({
                    value: item.value,
                    name: item.name,
                    itemStyle: {
                        color: colors[index],
                    },
                })),
            },
        ],
    };
};

/**
 * Function to get configuration options for a Products by Location chart.
 *
 * @param {object} dataDash2
 * @return {object} options
 */
export function getHealthCategoriesChartOptions(dataDash2) {
    if (
        !dataDash2 ||
        typeof dataDash2.categories_health === "undefined" ||
        dataDash2.categories_health.length === 0
    ) {
        return {};
    }

    const specificColors = [
        "#00968f",
        "#1b3240",
        "#ff6b35",
        "#dc3545",
        "#17a2b8",
        "#6f42c1",
        "#20c997",
        "#fd7e14",
        "#6610f2",
        "#28a745",
    ];

    const treemapData = dataDash2.categories_health.map((item, index) => ({
        name: item.category,
        value: item.productsCount,
        locations: item.locations,
        itemStyle: {
            color: specificColors[index % specificColors.length],
        },
    }));

    return {
        backgroundColor: "#ffffff",
        tooltip: {
            formatter: (params) => {
                const { data } = params;
                const locationsList =
                    data.locations && data.locations.length > 0
                        ? data.locations.join("<br/>")
                        : "No locations";

                return `
                        <div style="padding: 8px; font-family: Poppins;">
                            <b>${data.name}</b><br/>
                            <b>Products:</b> ${data.value}<br/>
                            <b>Locations:</b><br/>${locationsList}
                        </div>
                    `;
            },
            textStyle: {
                fontFamily: "Poppins",
            },
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            borderColor: "#eee",
            borderWidth: 1,
            extraCssText: "box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);",
        },

        series: [
            {
                name: "Categories",
                type: "treemap",
                width: "95%",
                height: "95%",
                left: "center",
                top: "center",
                data: treemapData,
                roam: false,
                nodeClick: false,
                breadcrumb: { show: false },
                label: {
                    show: false,
                    position: "inside",
                    formatter: function (params) {
                        const name = params.name;
                        const value = params.value;
                        const maxLength = 20;

                        const truncatedName =
                            name.length > maxLength
                                ? name.substring(0, maxLength) + "..."
                                : name;

                        return `${truncatedName}\n${value}`;
                    },
                    fontSize: function (params) {
                        const area =
                            params.treeAncestors[
                                params.treeAncestors.length - 1
                            ].area;
                        if (area < 2000) return 10;
                        if (area < 5000) return 12;
                        if (area < 10000) return 14;
                        return 16;
                    },
                    fontFamily: "Poppins, sans-serif",
                    color: "#ffffff",
                    fontWeight: 500,
                    width: "90%",
                    overflow: "truncate",
                    lineHeight: 16,
                    align: "center",
                    verticalAlign: "middle",
                },
                itemStyle: {
                    borderColor: "#fff",
                    borderWidth: 2,
                    gapWidth: 2,
                    borderRadius: 4,
                },
                emphasis: {
                    itemStyle: {
                        shadowBlur: 10,
                        shadowColor: "rgba(0,0,0,0.3)",
                    },
                    label: {
                        fontWeight: "bold",
                        fontSize: function (params) {
                            const area =
                                params.treeAncestors[
                                    params.treeAncestors.length - 1
                                ].area;
                            if (area < 2000) return 11;
                            if (area < 5000) return 13;
                            if (area < 10000) return 15;
                            return 17;
                        },
                    },
                },
                levels: [
                    {
                        itemStyle: {
                            borderWidth: 2,
                            gapWidth: 2,
                            borderColor: "#fff",
                        },
                    },
                ],
            },
        ],
    };
}

/**
 * Function to get configuration options for a Neurotoxic chart.
 *
 * @param {object} dataDash2
 * @return {object} options
 */
export function getNeurotoxicChartOptions(dataDash2) {
    if (
        !dataDash2 ||
        typeof dataDash2.levels_neurotoxic === "undefined" ||
        typeof dataDash2.product_neurotoxic_count === "undefined"
    ) {
        return {};
    }

    const categories = ["Level 1", "Level 2", "Level 3", "Level 4", "Others"];
    const data = [
        dataDash2.levels_neurotoxic.level_1 || 0,
        dataDash2.levels_neurotoxic.level_2 || 0,
        dataDash2.levels_neurotoxic.level_3 || 0,
        dataDash2.levels_neurotoxic.level_4 || 0,
        dataDash2.levels_neurotoxic.level_for_sga || 0,
    ];

    return {
        title: {
            text: `${dataDash2.product_neurotoxic_count} Neurotoxic`,
            left: "20px",
            top: "10px",
            textStyle: {
                fontFamily: "Poppins",
                fontSize: "16px",
                fontWeight: 500,
                color: "#333",
                rich: {
                    total: {
                        fontSize: "24px",
                        fontWeight: 600,
                        padding: [0, 8, 0, 0],
                    },
                },
            },
        },
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: "shadow",
            },
            formatter: "{b}: {c}",
        },
        legend: {
            data: ["Neurotoxicity levels"],
            textStyle: {
                fontFamily: "Poppins",
                color: "#333",
            },
            top: "5%",
            icon: "circle",
        },
        grid: {
            top: "15%",
            left: "5%",
            right: "5%",
            bottom: "10%",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: categories,
            axisLabel: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 12,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: true,
                lineStyle: {
                    color: "#E0E0E0",
                },
            },
        },
        yAxis: {
            type: "value",
            splitLine: {
                lineStyle: {
                    type: "dashed",
                    color: "#E0E0E0",
                },
            },
            axisLabel: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 12,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: false,
            },
        },
        series: [
            {
                type: "bar",
                data: data.map((value, index) => ({
                    value: value,
                    itemStyle: {
                        color: getBarColor(index),
                        borderRadius: [15, 15, 0, 0],
                    },
                })),
                barWidth: "40%",
                emphasis: {
                    itemStyle: {
                        brightness: 0.1,
                    },
                },
            },
        ],
    };
}

/**
 * Function to get the color of a bar.
 *
 * @param {number} index
 * @return {string} color
 */
function getBarColor(index) {
    const colors = [
        "#068190",
        "#04373D",
        "#674485",
        "#117ACB",
        "#B45DFF",
    ];
    return colors[index];
}

/**
 * Function to get configuration options for a Corrosive Effects chart.
 *
 * @param {object} dataDash2
 * @return {object} options
 */
export function getCorrosiveChartOptions(dataDash2) {
    if (
        !dataDash2.product_corrosive_ocular_count === "undefined" ||
        !dataDash2.product_corrosive_dermal_count === "undefined"
    ) {
        return {};
    }

    return {
        title: {
            text: `${dataDash2.product_corrosive_total || 0} Corrosive products`,
            left: "20px",
            top: "10px",
            textStyle: {
                fontFamily: "Poppins",
                fontSize: "16px",
                fontWeight: 500,
                color: "#333",
            },
        },
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: "shadow",
            },
            textStyle: {
                fontFamily: "Poppins",
            },
        },
        legend: {
            show: true,
            bottom: "0%",
            left: "center",
            itemWidth: 10,
            itemHeight: 10,
            textStyle: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 14,
            },
            itemGap: 30,
            icon: "circle",
        },
        grid: {
            top: "25%",
            left: "3%",
            right: "4%",
            bottom: "15%",
            containLabel: true,
        },
        xAxis: {
            type: "value",
            splitLine: {
                lineStyle: {
                    type: "dashed",
                    color: "#E0E0E0",
                },
            },
            axisLabel: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 12,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: false,
            },
        },
        yAxis: {
            type: "category",
            data: ["", ""],
            axisLabel: {
                show: false,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: true,
                lineStyle: {
                    color: "#E0E0E0",
                },
            },
        },
        series: [
            {
                name: "Eyes",
                type: "bar",
                stack: "total",
                data: [dataDash2.product_corrosive_ocular_count || 0, 0],
                itemStyle: {
                    color: "#04373D",
                    borderRadius: [0, 15, 15, 0],
                },
                barWidth: "40%",
            },
            {
                name: "Skin",
                type: "bar",
                stack: "total",
                data: [0, dataDash2.product_corrosive_dermal_count || 0],
                itemStyle: {
                    color: "#0FBEF9",
                    borderRadius: [0, 15, 15, 0],
                },
                barWidth: "40%",
            },
        ],
    };
}

/**
 * Function to get configuration options for a Sensitizers chart.
 *
 * @param {object} dataDash2
 * @return {object} options
 */
export function getSensitizingChartOptions(dataDash2) {
    if (
        !dataDash2 ||
        typeof dataDash2.sga_sensitizing_dermal_count === "undefined" ||
        typeof dataDash2.sga_sensitizing_respiratory_count === "undefined"
    ) {
        return {};
    }

    const categories = ["Dermal", "Respiratory"];
    const data = [
        dataDash2.sga_sensitizing_dermal_count || 0,
        dataDash2.sga_sensitizing_respiratory_count || 0,
    ];

    return {
        title: {
            text: `${
                dataDash2.sga_sensitizing_dermal_count +
                dataDash2.sga_sensitizing_respiratory_count
            } Sensitizers`,
            left: "20px",
            top: "10px",
            textStyle: {
                fontFamily: "Poppins",
                fontSize: "16px",
                fontWeight: 500,
                color: "#333",
                rich: {
                    total: {
                        fontSize: "24px",
                        fontWeight: 600,
                        padding: [0, 8, 0, 0],
                    },
                },
            },
        },
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: "shadow",
            },
            formatter: "{b}: {c}",
        },
        legend: {
            data: ["Sensitizers"],
            textStyle: {
                fontFamily: "Poppins",
                color: "#333",
            },
            top: "5%",
            icon: "circle",
        },
        grid: {
            top: "15%",
            left: "5%",
            right: "5%",
            bottom: "10%",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: categories,
            axisLabel: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 12,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: true,
                lineStyle: {
                    color: "#E0E0E0",
                },
            },
        },
        yAxis: {
            type: "value",
            splitLine: {
                lineStyle: {
                    type: "dashed",
                    color: "#E0E0E0",
                },
            },
            axisLabel: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 12,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: false,
            },
        },
        series: [
            {
                type: "bar",
                data: data.map((value, index) => ({
                    value: value,
                    itemStyle: {
                        color: getBarColor(index),
                        borderRadius: [15, 15, 0, 0],
                    },
                })),
                barWidth: "17%",
                emphasis: {
                    itemStyle: {
                        brightness: 0.1,
                    },
                },
            },
        ],
    };
}

/**
 * Function to get configuration options for an Inventory Status chart.
 *
 * @param {object} dataDash1
 * @return {object} options
 */
export function getStatusInventoryPQChartOptions(dataDash1) {
    if (
        !dataDash1 ||
        typeof dataDash1.total_inventory_verified === "undefined" ||
        typeof dataDash1.total_inventory_for_verification_cumply === "undefined" ||
        typeof dataDash1.total_inventory_for_verification_not_cumply === "undefined" ||
        typeof dataDash1.not_document === "undefined"
    ) {
        return {};
    }

    const series = [
        {
            name: "Characterized",
            value: dataDash1.total_inventory_verified,
            color: "#35C895",
        },
        {
            name: "Uncharacterized compliant",
            value: dataDash1.total_inventory_for_verification_cumply,
            color: "#0FBEF9",
        },
        {
            name: "Uncharacterized non-compliant",
            value: dataDash1.total_inventory_for_verification_not_cumply,
            color: "#F27833",
        },
        {
            name: "No document",
            value: dataDash1.not_document,
            color: "#E84432",
        },
    ];

    const maxValue = Math.max(...series.map((item) => item.value));
    const yAxisMax = Math.ceil(maxValue * 1.2);

    return {
        tooltip: {
            trigger: "item",
            axisPointer: {
                type: "none",
            },
            textStyle: {
                fontFamily: "Poppins",
            },
        },
        grid: {
            left: "3%",
            right: "4%",
            bottom: "25%",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: [""],
            axisLabel: {
                show: false,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: false,
            },
        },
        yAxis: {
            type: "value",
            max: yAxisMax,
            splitNumber: 6,
            splitLine: {
                lineStyle: {
                    type: "dashed",
                    color: "#E0E0E0",
                },
            },
            axisLabel: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 12,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: false,
            },
        },
        legend: {
            data: series.map((item) => item.name),
            top: "0%",
            left: "center",
            itemWidth: 8,
            itemHeight: 8,
            textStyle: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 11,
            },
            itemGap: 5,
            icon: "circle",
            formatter: function (name) {
                const item = series.find((s) => s.name === name);
                return `${name} (${item.value})`;
            },
        },
        series: series.map((item) => ({
            name: item.name,
            type: "bar",
            data: [item.value],
            itemStyle: {
                color: item.color,
                borderRadius: [15, 15, 0, 0],
            },
            barWidth: 25,
            barGap: 1.5,
            label: {
                show: true,
                position: "top",
                distance: 5,
                fontFamily: "Poppins",
                fontSize: 12,
                color: "#333",
            },
        })),
    };
}

/**
 * Function to get configuration options for an Inventory Hazard chart.
 *
 * @param {object} dataDash1
 * @return {object} options
 */
export function getRiskInventoryChartOptions(dataDash1) {
    if (
        !dataDash1 ||
        typeof dataDash1.sga_physical_total === "undefined" ||
        typeof dataDash1.sga_health_total === "undefined" ||
        typeof dataDash1.sga_environment_total === "undefined"
    ) {
        return {};
    }

    const data = [
        {
            value: dataDash1.sga_physical_total,
            name: "Physical",
            itemStyle: {
                color: "#0FBEF9",
            },
        },
        {
            value: dataDash1.sga_health_total,
            name: "Health",
            itemStyle: {
                color: "#1C7FCB",
            },
        },
        {
            value: dataDash1.sga_environment_total,
            name: "Environmental",
            itemStyle: {
                color: "#35C895",
            },
        },
    ];

    return {
        tooltip: {
            trigger: "item",
            textStyle: {
                fontFamily: "Poppins",
            },
        },
        legend: {
            top: "0%",
            left: "center",
            itemWidth: 8,
            itemHeight: 8,
            textStyle: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 11,
            },
            itemGap: 5,
            icon: "circle",
            formatter: function (name) {
                const item = data.find((s) => s.name === name);
                return `${name} (${item.value})`;
            },
        },
        series: [
            {
                type: "pie",
                radius: ["40%", "70%"],
                avoidLabelOverlap: false,
                itemStyle: {
                    borderRadius: 6,
                    borderColor: "#fff",
                    borderWidth: 2,
                },
                label: {
                    show: false,
                    position: "inside",
                    formatter: "{c}",
                    borderWidth: 1,
                    borderRadius: 4,
                    fontSize: 14,
                    fontWeight: "bold",
                    color: "#fff",
                    textShadowColor: "rgba(0, 0, 0, 0.5)",
                    textShadowBlur: 3,
                    textShadowOffsetX: 1,
                    textShadowOffsetY: 1,
                    fontFamily: "Poppins",
                },
                labelLine: {
                    show: false,
                },
                emphasis: {
                    label: {
                        show: true,
                        fontSize: 16,
                        fontWeight: "bold",
                        fontFamily: "Poppins",
                    },
                    itemStyle: {
                        shadowBlur: 10,
                        shadowOffsetX: 0,
                        shadowColor: "rgba(0, 0, 0, 0.5)",
                    },
                },
                data: data,
            },
        ],
    };
}

/**
 * Function to get configuration options for a Use and Disuse chart.
 *
 * @param {object} dataDash1
 * @return {object} options
 */
export function getUseandDesuseChartOptions(dataDash1) {
    if (
        !dataDash1 ||
        typeof dataDash1.used === "undefined" ||
        typeof dataDash1.not_use === "undefined"
    ) {
        return {};
    }

    const data = [
        {
            value: dataDash1.used,
            name: "Yes",
            itemStyle: {
                color: "#0FBEF9",
            },
        },
        {
            value: dataDash1.not_use,
            name: "No",
            itemStyle: {
                color: "#1C7FCB",
            },
        },
    ];

    return {
        tooltip: {
            trigger: "item",
            textStyle: {
                fontFamily: "Poppins",
            },
        },
        legend: {
            top: "0%",
            left: "center",
            itemWidth: 8,
            itemHeight: 8,
            textStyle: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 11,
            },
            itemGap: 5,
            icon: "circle",
            formatter: function (name) {
                const item = data.find((s) => s.name === name);
                return `${name} (${item.value})`;
            },
        },
        series: [
            {
                type: "pie",
                radius: ["40%", "70%"],
                avoidLabelOverlap: false,
                itemStyle: {
                    borderRadius: 6,
                    borderColor: "#fff",
                    borderWidth: 2,
                },
                label: {
                    show: false,
                    position: "inside",
                    formatter: "{c}",
                    borderWidth: 1,
                    borderRadius: 4,
                    fontSize: 14,
                    fontWeight: "bold",
                    color: "#fff",
                    textShadowColor: "rgba(0, 0, 0, 0.5)",
                    textShadowBlur: 3,
                    textShadowOffsetX: 1,
                    textShadowOffsetY: 1,
                    fontFamily: "Poppins",
                },
                labelLine: {
                    show: false,
                },
                emphasis: {
                    label: {
                        show: true,
                        fontSize: 16,
                        fontWeight: "bold",
                        fontFamily: "Poppins",
                    },
                    itemStyle: {
                        shadowBlur: 10,
                        shadowOffsetX: 0,
                        shadowColor: "rgba(0, 0, 0, 0.5)",
                    },
                },
                data: data,
            },
        ],
    };
}

/**
 * Function to get configuration options for a bar chart of products subject to regulation.
 *
 * @param {object} dataDash1
 * @return {object} options
 */
export function getOptionsBarsSuggestionRegulation(dataDash1) {
    if (
        !dataDash1 ||
        typeof dataDash1.sga_death_total === "undefined" ||
        typeof dataDash1.carcinogenic_total === "undefined" ||
        typeof dataDash1.un_danger_total === "undefined" ||
        typeof dataDash1.ppam_total === "undefined"
    ) {
        return {};
    }

    const data = [
        dataDash1.sga_death_total,
        dataDash1.carcinogenic_total,
        dataDash1.un_danger_total,
        dataDash1.ppam_total,
    ];

    return {
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: "shadow",
            },
        },
        grid: {
            left: "3%",
            right: "3%",
            bottom: "3%",
            top: "25%",
            containLabel: true,
        },
        xAxis: {
            type: "value",
            boundaryGap: [0, 0.01],
        },
        yAxis: {
            type: "category",
            data: [
                "Lethal",
                "Carcinogenic",
                "Dangerous goods",
                "PPAM",
            ],
            axisLabel: {
                fontSize: 10,
            },
        },
        series: [
            {
                name: "Quantity",
                type: "bar",
                data: data,
                itemStyle: {
                    color: function (params) {
                        const colors = [
                            "#0FBEF9",
                            "#E84432",
                            "#F27833",
                            "#35C895",
                        ];
                        return colors[params.dataIndex];
                    },
                    borderColor: "#fff",
                    borderWidth: 1,
                    borderRadius: [0, 15, 15, 0],
                },
                label: {
                    show: true,
                    position: "",
                },
            },
        ],
    };
}

/**
 * Function to get configuration options for a Benefits by Year chart.
 *
 * @param {object} dataDash1
 * @return {object} options
 */
export function getBenefitForYearsChartOptions(dataDash1) {
    if (
        !dataDash1 ||
        !dataDash1.benefirs_for_years ||
        !dataDash1.total_benefits
    ) {
        return {};
    }

    const years = Object.keys(dataDash1.benefirs_for_years);
    const values = Object.values(dataDash1.benefirs_for_years);
    const totalBenefits = dataDash1.total_benefits;

    return {
        color: ["#36a2eb"],
        grid: {
            left: "3%",
            right: "4%",
            bottom: "3%",
            top: "15%",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: years,
            axisLine: {
                lineStyle: {
                    color: "#ddd",
                },
            },
            axisLabel: {
                color: "#666",
            },
        },
        yAxis: {
            type: "value",
            splitLine: {
                lineStyle: {
                    type: "dashed",
                    color: "#ddd",
                },
            },
            axisLabel: {
                color: "#666",
            },
        },
        tooltip: {
            trigger: "item",
            textStyle: {
                fontFamily: "Poppins",
            },
        },
        legend: {
            top: "0%",
            left: "center",
            itemWidth: 8,
            itemHeight: 8,
            textStyle: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 11,
            },
            itemGap: 5,
            icon: "circle",
            formatter: function (name) {
                return `Beneficiaries (${totalBenefits})`;
            },
        },
        toolbox: {
            show: true,
            itemSize: 15,
            itemGap: 10,
            right: "10%",
            feature: {
                magicType: {
                    type: ["line", "bar"],
                    title: {
                        line: "Line chart",
                        bar: "Bar chart",
                    },
                },
                restore: {
                    title: "Restore",
                },
                saveAsImage: {
                    pixelRatio: 2,
                    title: "Save as image",
                },
            },
        },
        series: [
            {
                name: "Beneficiaries",
                data: values,
                type: "line",
                smooth: true,
                symbol: "circle",
                symbolSize: 6,
                lineStyle: {
                    width: 3,
                },
                itemStyle: {
                    borderRadius: [15, 15, 0, 0],
                },
            },
        ],
        tooltip: {
            trigger: "axis",
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            borderColor: "#eee",
            borderWidth: 1,
            padding: [10, 15],
            textStyle: {
                color: "#333",
                fontFamily: "Poppins",
                fontSize: 13,
            },
            formatter: function (params) {
                const data = params[0];
                return `
                    <div style="display: flex; align-items: center; margin-bottom: 5px;">
                        <span style="font-weight: bold; font-size: 14px; color: #333;">${data.name}</span>
                    </div>
                    <div style="display: flex; align-items: center; margin-top: 8px;">
                        <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${data.color}; margin-right: 8px;"></span>
                        <span style="color: #666;">Beneficiaries:</span>
                        <span style="font-weight: bold; margin-left: 5px; color: #333;">${data.value}</span>
                    </div>
                `;
            },
            extraCssText:
                "box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1); border-radius: 8px;",
        },
    };
}

/**
 * Function to get configuration options for a Risk Analysis chart.
 *
 * @param {object} dataDash1
 * @return {object} options
 */
export function getRiskAnalysisChartOptions(dataDash1) {
    if (!dataDash1 || !dataDash1.priority || !dataDash1.priority.total) {
        return {};
    }

    const series = [
        {
            name: "High Priority",
            value: dataDash1.priority.priority_1,
            color: "#E84432",
        },
        {
            name: "Medium Priority",
            value: dataDash1.priority.priority_2,
            color: "#F9D70F",
        },
        {
            name: "Low Priority",
            value: dataDash1.priority.priority_3,
            color: "#35C895",
        },
    ];

    const filteredTotal = series.reduce((sum, item) => sum + item.value, 0);
    const totalTasks = dataDash1.priority.total || filteredTotal;

    return {
        color: series.map((item) => item.color),
        grid: {
            left: "5%",
            right: "10%",
            bottom: "5%",
            top: "25%",
            containLabel: true,
        },
        tooltip: {
            trigger: "item",
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            borderColor: "#eee",
            borderWidth: 1,
            padding: [10, 15],
            textStyle: {
                color: "#333",
                fontFamily: "Poppins, sans-serif",
                fontSize: 13,
            },
            formatter: function (params) {
                return `
            <div style="display: flex; align-items: center; margin-bottom: 5px;">
              <span style="font-weight: bold; font-size: 14px; color: #333;">${params.name}</span>
            </div>
            <div style="display: flex; align-items: center; margin-top: 8px;">
              <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${params.color}; margin-right: 8px;"></span>
              <span style="color: #666;">Quantity:</span>
              <span style="font-weight: bold; margin-left: 5px; color: #333;">${params.value}</span>
              <span style="color: #666; margin-left: 5px;">(${Math.round((params.value / totalTasks) * 100)}%)</span>
            </div>
          `;
            },
            extraCssText:
                "box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1); border-radius: 8px;",
        },
        legend: {
            top: "0%",
            left: "center",
            itemWidth: 8,
            itemHeight: 8,
            textStyle: {
                fontFamily: "Poppins, sans-serif",
                color: "#666",
                fontSize: 11,
            },
            itemGap: 15,
            icon: "circle",
            formatter: function (name) {
                const item = series.find((s) => s.name === name);
                if (item) {
                    return `${name} (${item.value})`;
                }
                return name;
            },
            data: series.map((item) => item.name),
        },
        toolbox: {
            show: true,
            itemSize: 15,
            itemGap: 10,
            right: "10%",
            feature: {
                magicType: {
                    type: ["bar", "pie", "line"],
                    title: {
                        bar: "Bar chart",
                        pie: "Pie chart",
                        line: "Line chart",
                    },
                },
                restore: {
                    title: "Restore",
                },
                saveAsImage: {
                    pixelRatio: 2,
                    title: "Save as image",
                },
            },
        },
        title: {
            text: `Viewing ${filteredTotal} tasks out of a total of ${totalTasks}`,
            textStyle: {
                fontSize: 16,
                fontWeight: "normal",
                color: "#333",
                fontFamily: "Poppins, sans-serif",
            },
            left: "center",
            top: "30px",
            subtext: `${Math.round((filteredTotal / totalTasks) * 100)}% of the total shown`,
            subtextStyle: {
                fontSize: 12,
                color: "#666",
                fontFamily: "Poppins, sans-serif",
            },
        },
        xAxis: {
            type: "category",
            data: series.map((item) => item.name),
            axisLine: {
                lineStyle: {
                    color: "#ddd",
                },
            },
            axisLabel: {
                color: "#666",
                fontFamily: "Poppins, sans-serif",
                fontSize: 12,
                interval: 0,
                rotate: 0,
            },
        },
        yAxis: {
            type: "value",
            splitLine: {
                lineStyle: {
                    type: "dashed",
                    color: "#ddd",
                },
            },
            axisLabel: {
                color: "#666",
                fontFamily: "Poppins, sans-serif",
                fontSize: 12,
            },
        },
        series: [
            {
                name: "Priority",
                type: "bar",
                barWidth: "30%",
                data: series.map((item) => ({
                    value: item.value,
                    name: item.name,
                    itemStyle: {
                        color: item.color,
                    },
                })),
                emphasis: {
                    itemStyle: {
                        shadowBlur: 10,
                        shadowOffsetX: 0,
                        shadowColor: "rgba(0, 0, 0, 0.5)",
                    },
                },
                itemStyle: {
                    borderRadius: [15, 15, 0, 0],
                    borderColor: "#fff",
                    borderWidth: 1,
                },
                label: {
                    show: true,
                    position: "top",
                    formatter: "{c}",
                    fontSize: 12,
                    fontFamily: "Poppins, sans-serif",
                    color: "#666",
                },
                smooth: true,
                lineStyle: {
                    color: "#36A2EB",
                    width: 3,
                },
                symbolSize: 10,
            },
        ],
    };
}

/**
 * Function to get configuration options for an Acute Toxicity - Toxic chart.
 *
 * @param {object} dataDash2
 * @return {object} options
 */
export function getAgutetoxicityChartOptions(dataDash2) {
    if (
        !dataDash2 ||
        typeof dataDash2.product_toxicity_agude_ingestion_count === "undefined" ||
        typeof dataDash2.product_toxicity_inhalation_count === "undefined" ||
        typeof dataDash2.product_toxicity_dermal_count === "undefined"
    ) {
        return {};
    }

    const series = [
        {
            name: "Ingestion",
            value: dataDash2.product_toxicity_agude_ingestion_count,
            color: "#04373D",
        },
        {
            name: "Dermal",
            value: dataDash2.product_toxicity_dermal_count,
            color: "#117ACB",
        },
        {
            name: "Inhalation",
            value: dataDash2.product_toxicity_inhalation_count,
            color: "#0FBEF9",
        },
    ];

    const maxValue = Math.max(...series.map((item) => item.value));
    const yAxisMax = Math.ceil(maxValue * 1.2);

    return {
        tooltip: {
            trigger: "item",
            axisPointer: {
                type: "none",
            },
            textStyle: {
                fontFamily: "Poppins",
            },
        },
        grid: {
            left: "3%",
            right: "4%",
            bottom: "25%",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: [""],
            axisLabel: {
                show: false,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: false,
            },
        },
        yAxis: {
            type: "value",
            max: yAxisMax,
            splitNumber: 6,
            splitLine: {
                lineStyle: {
                    type: "dashed",
                    color: "#E0E0E0",
                },
            },
            axisLabel: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 12,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: false,
            },
        },
        legend: {
            data: series.map((item) => item.name),
            top: "0%",
            left: "center",
            itemWidth: 8,
            itemHeight: 8,
            textStyle: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 11,
            },
            itemGap: 5,
            icon: "circle",
            formatter: function (name) {
                const item = series.find((s) => s.name === name);
                return `${name} (${item.value})`;
            },
        },
        series: series.map((item) => ({
            name: item.name,
            type: "bar",
            data: [item.value],
            itemStyle: {
                color: item.color,
                borderRadius: [15, 15, 0, 0],
            },
            barWidth: '8%',
            barGap: 1.5,
            label: {
                show: true,
                position: "top",
                distance: 5,
                fontFamily: "Poppins",
                fontSize: 12,
                color: "#333",
            },
        })),
    };
}

/**
 * Function to get configuration options for a Target Organs chart.
 *
 * @param {object} dataDash2
 * @return {object} options
 */
export function getTargetOrgansChartOptions(dataDash2) {
    if (
        !dataDash2 ||
        typeof dataDash2.product_target_organs_exposition_repetitive_count === "undefined" ||
        typeof dataDash2.product_target_organs_exposition_unique_count === "undefined"
    ) {
        return {};
    }

    const series = [
        {
            name: "Repeated exposure",
            value: dataDash2.product_target_organs_exposition_repetitive_count,
            color: "#117ACB",
        },
        {
            name: "Single exposure",
            value: dataDash2.product_target_organs_exposition_unique_count,
            color: "#0FBEF9",
        },
    ];

    const maxValue = Math.max(...series.map((item) => item.value));
    const yAxisMax = Math.ceil(maxValue * 1.2);

    return {
        tooltip: {
            trigger: "item",
            axisPointer: {
                type: "none",
            },
            textStyle: {
                fontFamily: "Poppins",
            },
        },
        grid: {
            left: "3%",
            right: "4%",
            bottom: "25%",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: [""],
            axisLabel: {
                show: false,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: false,
            },
        },
        yAxis: {
            type: "value",
            max: yAxisMax,
            splitNumber: 6,
            splitLine: {
                lineStyle: {
                    type: "dashed",
                    color: "#E0E0E0",
                },
            },
            axisLabel: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 12,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: false,
            },
        },
        legend: {
            data: series.map((item) => item.name),
            top: "0%",
            left: "center",
            itemWidth: 8,
            itemHeight: 8,
            textStyle: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 11,
            },
            itemGap: 5,
            icon: "circle",
            formatter: function (name) {
                const item = series.find((s) => s.name === name);
                return `${name} (${item.value})`;
            },
        },
        toolbox: {
            show: true,
            itemSize: 15,
            itemGap: 10,
            right: "10%",
            feature: {
                saveAsImage: {
                    pixelRatio: 2,
                    title: "Save as image",
                },
            },
        },
        series: series.map((item) => ({
            name: item.name,
            type: "bar",
            data: [item.value],
            itemStyle: {
                color: item.color,
                borderRadius: [15, 15, 0, 0],
            },
            barWidth: '8%',
            barGap: 1.5,
            label: {
                show: true,
                position: "top",
                distance: 5,
                fontFamily: "Poppins",
                fontSize: 12,
                color: "#333",
            },
        })),
    };
}

/**
 * Function to get configuration options for a Normative Components chart.
 *
 * @param {object} dataDash2
 * @return {object} options
 */
export function getComponentForNormativeOptions(dataDash2) {
    if (
        !dataDash2 ||
        typeof dataDash2.component_active_for_normative === "undefined"
    ) {
        return {};
    }

    const filteredData = dataDash2.component_active_for_normative
        .filter(item => item.count > 0)
        .sort((a, b) => b.count - a.count)
        .map(item => ({
            ...item,
            name: item.name.toUpperCase()
        }));

    const colors = [
        '#117ACB', '#0FBEF9', '#ff6b6b', '#4cc9f0',
        '#4895ef', '#560bad', '#f72585', '#7209b7',
        '#3a0ca3', '#3f37c9', '#117ACB', '#4895ef'
    ];

    return {
        tooltip: {
            trigger: 'item',
            formatter: (params) => {
                return `${params.name}<br/>Quantity: ${params.value}`;
            }
        },
        legend: {
            orient: 'horizontal',
            bottom: '5%',
            left: 'center',
            type: 'scroll',
            width: '90%',
            formatter: (name) => {
                const item = filteredData.find(d => d.name === name);
                return name + ' (' + item.count + ')';
            },
            textStyle: {
                fontSize: 12,
                fontFamily: 'Arial, sans-serif',
                color: '#333',
                overflow: 'breakAll',
                width: 150
            },
            icon: 'circle',
            itemGap: 15,
            itemWidth: 10,
            itemHeight: 10,
            pageTextStyle: {
                color: '#333'
            }
        },
        series: [{
            name: 'Components',
            type: 'pie',
            radius: ['40%', '70%'],
            center: ['50%', '45%'],
            avoidLabelOverlap: false,
            itemStyle: {
                borderRadius: 6,
                borderColor: "#fff",
                borderWidth: 2,
            },
            label: {
                show: true,
                position: 'inside',
                formatter: '{c}',
                fontSize: 14,
                fontWeight: 'bold',
                color: '#fff',
                textShadowColor: 'rgba(0, 0, 0, 0.5)',
                textShadowBlur: 3,
                textShadowOffsetX: 1,
                textShadowOffsetY: 1
            },
            labelLine: {
                show: false
            },
            emphasis: {
                scale: false,
                itemStyle: {
                    shadowBlur: 10,
                    shadowOffsetX: 0,
                    shadowColor: 'rgba(0, 0, 0, 0.5)'
                }
            },
            data: filteredData.map((item, index) => ({
                value: item.count,
                name: item.name,
                itemStyle: {
                    color: colors[index % colors.length]
                }
            }))
        }]
    };
}